import { useState } from 'react';
import {
  makeStyles, shorthands, tokens, Text, Badge, Button, SearchBox,
  Accordion, AccordionItem, AccordionHeader, AccordionPanel,
  Table, TableHeader, TableRow, TableHeaderCell, TableBody, TableCell,
} from '@fluentui/react-components';
import { SeverityBadge, Empty } from './common';
import { DispositionControl, DispositionBadge } from './DispositionControl';
import { ResolutionPanel } from './ResolutionPanel';
import { SEVERITY_ORDER } from '../theme/pillars';
import type { Finding, Severity, Disposition } from '../api/client';

const useStyles = makeStyles({
  filters: { display: 'flex', flexWrap: 'wrap', columnGap: tokens.spacingHorizontalS, rowGap: tokens.spacingVerticalS, marginBottom: tokens.spacingVerticalM },
  head: { display: 'flex', alignItems: 'center', columnGap: tokens.spacingHorizontalS, flexWrap: 'wrap' },
  muted: { color: tokens.colorNeutralForeground3, fontSize: tokens.fontSizeBase200 },
  block: { display: 'flex', flexDirection: 'column', rowGap: tokens.spacingVerticalS, ...shorthands.padding(tokens.spacingVerticalS, 0) },
  label: {
    color: tokens.colorNeutralForeground2, fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightSemibold, textTransform: 'uppercase', letterSpacing: '0.04em',
  },
  held: { fontFamily: tokens.fontFamilyMonospace, fontSize: tokens.fontSizeBase200, wordBreak: 'break-word' },
  tableWrap: {
    // The table scrolls inside its card rather than widening the page. Without
    // this, the resource table with five columns pushes the layout past the
    // viewport and the whole page scrolls sideways.
    width: '100%',
    ...shorthands.overflow('auto', 'hidden'),
    overflowX: 'auto',
  },
  steps: {
    marginTop: tokens.spacingVerticalXS, marginBottom: tokens.spacingVerticalXS,
    paddingLeft: tokens.spacingHorizontalXL,
    display: 'flex', flexDirection: 'column', rowGap: tokens.spacingVerticalXS,
    fontSize: tokens.fontSizeBase300, lineHeight: tokens.lineHeightBase300,
  },
});

export interface FindingListProps {
  findings: Finding[];
  tenantId: string;
  emptyMessage?: string;
}

/**
 * The findings themselves. Shared by the pillar pages and the run view so a
 * finding looks and behaves identically wherever it is read.
 */
export function FindingList({ findings, tenantId, emptyMessage }: FindingListProps) {
  const styles = useStyles();
  const [severity, setSeverity] = useState<Severity | null>(null);
  const [query, setQuery] = useState('');
  const [openItems, setOpenItems] = useState<string[]>([]);
  // Saved decisions are held here so the panel updates without refetching the
  // whole page, which would collapse every expanded finding.
  const [saved, setSaved] = useState<Record<string, Disposition>>({});
  const dispositionFor = (f: Finding) => saved[f.checkId] ?? f.disposition;

  const counts = SEVERITY_ORDER.map((s) => ({ s, n: findings.filter((f) => f.severity === s).length }));

  // Search covers the affected resource names as well as the finding text.
  // During an investigation the question is usually "where does this account
  // appear", and a search that only reads titles cannot answer it.
  const needle = query.trim().toLowerCase();
  const matching = needle
    ? findings.filter((f) =>
        [f.title, f.detail, f.recommendation, f.checkId, f.area]
          .some((v) => v?.toLowerCase().includes(needle))
        || f.resources.some((r) =>
          [r.name, r.identifier, r.held, r.context, r.resourceType]
            .some((v) => v?.toLowerCase().includes(needle))))
    : findings;

  const shown = severity ? matching.filter((f) => f.severity === severity) : matching;

  // Most urgent first. A list ordered by anything else buries the one thing the
  // reader needed to see.
  const ordered = [...shown].sort(
    (a, b) => SEVERITY_ORDER.indexOf(a.severity) - SEVERITY_ORDER.indexOf(b.severity)
      || a.checkId.localeCompare(b.checkId),
  );

  if (findings.length === 0) {
    return <Empty>{emptyMessage ?? 'No findings.'}</Empty>;
  }

  return (
    <>
      <div className={styles.filters}>
        <SearchBox
          placeholder="Search findings and affected resources"
          value={query}
          onChange={(_, d) => setQuery(d.value)}
          style={{ minWidth: '300px' }}
        />
        <Button size="small" appearance={severity === null ? 'primary' : 'secondary'}
                onClick={() => setSeverity(null)}>
          All ({matching.length})
        </Button>
        {counts.map(({ s, n }) => (
          <Button key={s} size="small" disabled={n === 0 && severity !== s}
                  appearance={severity === s ? 'primary' : 'secondary'}
                  onClick={() => setSeverity(s)}>
            {s} ({n})
          </Button>
        ))}
      </div>

      {needle && (
        <Text className={styles.muted} style={{ display: 'block', marginBottom: tokens.spacingVerticalS }}>
          {matching.length} of {findings.length} finding(s) match, searching titles,
          detail and affected resource names.
        </Text>
      )}

      {ordered.length === 0 ? (
        <Empty>Nothing matches that search at this severity.</Empty>
      ) : (
      <Accordion multiple collapsible openItems={openItems}
                 onToggle={(_, d) => setOpenItems(d.openItems as string[])}>
        {ordered.map((f) => (
          <AccordionItem key={f.findingId} value={String(f.findingId)}>
            <AccordionHeader>
              <div className={styles.head}>
                <SeverityBadge severity={f.severity} />
                <Text weight="semibold">{f.title}</Text>
                <Badge appearance="outline" size="small">{f.checkId}</Badge>
                <span className={styles.muted}>{f.area}</span>
                <DispositionBadge disposition={dispositionFor(f)} />
                {f.resources.length > 0 && (
                  <Badge appearance="tint" color="informative" size="small">
                    {f.resources.length} affected
                  </Badge>
                )}
              </div>
            </AccordionHeader>

            <AccordionPanel>
              <div className={styles.block}>
                {f.detail && (
                  <div>
                    <div className={styles.label}>What we found</div>
                    <Text size={300}>{f.detail}</Text>
                  </div>
                )}
                {f.recommendation && (
                  <div>
                    <div className={styles.label}>Recommendation</div>
                    <Text size={300}>{f.recommendation}</Text>
                  </div>
                )}

                {f.resources.length > 0 && (
                  <div>
                    <div className={styles.label}>Affected resources ({f.resources.length})</div>
                    {/* The entities, and what each one holds. A finding without
                        these is an observation rather than something an
                        engineer can act on. */}
                    <div className={styles.tableWrap}>
                    <Table size="extra-small" aria-label={`Resources for ${f.checkId}`}>
                      <TableHeader>
                        <TableRow>
                          <TableHeaderCell>Type</TableHeaderCell>
                          <TableHeaderCell>Name</TableHeaderCell>
                          <TableHeaderCell>Identifier</TableHeaderCell>
                          <TableHeaderCell>Roles, permissions or setting</TableHeaderCell>
                          <TableHeaderCell>Context</TableHeaderCell>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {f.resources.slice(0, 60).map((r, i) => (
                          <TableRow key={`${f.findingId}-${i}`}>
                            <TableCell>{r.resourceType}</TableCell>
                            <TableCell><Text weight="semibold" size={200}>{r.name}</Text></TableCell>
                            <TableCell><span className={styles.muted}>{r.identifier ?? ''}</span></TableCell>
                            <TableCell><span className={styles.held}>{r.held ?? ''}</span></TableCell>
                            <TableCell><span className={styles.muted}>{r.context ?? ''}</span></TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                    </div>
                    {f.resources.length > 60 && (
                      <Text className={styles.muted}>
                        Showing the first 60 of {f.resources.length}.
                      </Text>
                    )}
                  </div>
                )}

                {f.remediationSteps.length > 0 && (
                  <div>
                    <div className={styles.label}>
                      Remediation plan ({f.remediationSteps.length} steps)
                    </div>
                    <ol className={styles.steps}>
                      {f.remediationSteps.map((step, i) => <li key={i}>{step}</li>)}
                    </ol>
                    {f.remediationPortal && (
                      <Text className={styles.muted}>
                        Where to make the change: {f.remediationPortal}
                      </Text>
                    )}
                  </div>
                )}

                <ResolutionPanel tenantId={tenantId} primitiveId={f.primitiveId} />

                <DispositionControl
                  tenantId={tenantId}
                  checkId={f.checkId}
                  disposition={dispositionFor(f)}
                  onSaved={(d) => setSaved((prev) => ({ ...prev, [f.checkId]: d }))}
                />
              </div>
            </AccordionPanel>
          </AccordionItem>
        ))}
      </Accordion>
      )}
    </>
  );
}
